import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FioriLibraryPanel } from "@/components/roles/FioriLibraryPanel";
import { RoleTemplatesPanel } from "@/components/roles/RoleTemplatesPanel";
import { FioriCatalogRolesPanel } from "@/components/roles/FioriCatalogRolesPanel";
import { RoleToolsModal } from "@/components/roles/RoleToolsModal";
import { RoleEngineeringHero } from "@/components/roles/RoleEngineeringHero";
import { RoleStatsTiles } from "@/components/roles/RoleStatsTiles";
import { SoDRulebookPanel } from "@/components/roles/SoDRulebookPanel";
import { MassGenerationPanel } from "@/components/roles/MassGenerationPanel";
import { RoleLibraryPanel } from "@/components/roles/RoleLibraryPanel";
import { CrossRoleSimulationPanel } from "@/components/roles/CrossRoleSimulationPanel";
import { ConsolidationResultsPanel } from "@/components/roles/ConsolidationResultsPanel";
import { MigrateAllResultsPanel } from "@/components/roles/MigrateAllResultsPanel";
import { BestPracticeSweepPanel } from "@/components/roles/BestPracticeSweepPanel";
import { ModuleGate } from "@/components/common/ModuleGate";
import { useCapabilities } from "@/lib/capabilities";
import { TID } from "@/lib/testIds";

export default function RoleEngineering() {
    const { caps } = useCapabilities();
    // Dashboard drill-downs deep-link here: ?expand=<role_id> auto-expands a
    // role's SoD detail, ?rule=<rule_id> filters the library to roles that
    // violate that specific SoD rule (from the Conflict Heatmap).
    const [searchParams, setSearchParams] = useSearchParams();
    const [ruleFilter, setRuleFilter] = useState(searchParams.get("rule") || "");
    const [roles, setRoles] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [specs, setSpecs] = useState("");
    const [generating, setGenerating] = useState(false);
    const [consolidation, setConsolidation] = useState(null);
    const [consolidating, setConsolidating] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [describing, setDescribing] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [crossSod, setCrossSod] = useState(null);
    const [simulating, setSimulating] = useState(false);
    const [exporting, setExporting] = useState("");
    const [rulebook, setRulebook] = useState(null);
    const [showRulebook, setShowRulebook] = useState(false);
    const [showCritical, setShowCritical] = useState(false);
    const [bpFilter, setBpFilter] = useState("all");
    const [rulebookSearch, setRulebookSearch] = useState("");
    const [toolsRole, setToolsRole] = useState(null);
    const [toolsOpen, setToolsOpen] = useState(false);
    const [toolsInitialTab, setToolsInitialTab] = useState("org");
    const [showActvtSelector, setShowActvtSelector] = useState(false);
    const [defaultActvt, setDefaultActvt] = useState(new Set());
    const [verificationFilter, setVerificationFilter] = useState("all"); // all | unverified | verified
    const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
    const [migratingAll, setMigratingAll] = useState(false);
    const [migrateAllResult, setMigrateAllResult] = useState(null);

    const load = useCallback(async () => {
        try {
            const [r, s] = await Promise.all([
                api.get("/roles"),
                api.get("/roles-stats/overview"),
            ]);
            setRoles(r.data);
            setStats(s.data);
        } catch (e) {
            console.warn("Failed to load roles", e);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Deep-link: auto-expand the role a Dashboard drill-down pointed at.
    useEffect(() => {
        const expandId = searchParams.get("expand");
        if (expandId && roles.some((r) => r.id === expandId)) setExpanded(expandId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roles]);

    const clearRuleFilter = () => {
        setRuleFilter("");
        setSearchParams({}, { replace: true });
    };

    // SoD Rulebook detail is Admin-only (backend enforces 403 for non-admins) —
    // fetched independently so it never blocks the Role Library from loading.
    useEffect(() => {
        if (caps?.is_admin) {
            api.get("/roles/sod-matrix").then(({ data }) => setRulebook(data)).catch(() => setRulebook(null));
        } else {
            setRulebook(null);
        }
    }, [caps?.is_admin]);

    const loadTemplate = async () => {
        const { data } = await api.get("/roles/template");
        setSpecs(data.example);
        toast.success("Template loaded — edit and generate");
    };

    const generate = async () => {
        if (!specs.trim()) { toast.error("Paste at least one role definition"); return; }
        setGenerating(true);
        try {
            const { data } = await api.post("/roles/generate", {
                specs_text: specs,
                default_actvt: showActvtSelector ? Array.from(defaultActvt) : [],
            });
            const verifiedMsg = showActvtSelector && defaultActvt.size
                ? " · field-verified against real ACTVT values"
                : "";
            toast.success(`Generated ${data.length} role(s) with Secure-by-Design checks${verifiedMsg}`);
            setSpecs("");
            setConsolidation(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        } finally { setGenerating(false); }
    };

    const toggleDefaultActvt = (code) => {
        setDefaultActvt((prev) => {
            const next = new Set(prev);
            next.has(code) ? next.delete(code) : next.add(code);
            return next;
        });
    };

    const remove = async (id) => {
        try {
            await api.delete(`/roles/${id}`);
            setRoles((prev) => prev.filter((r) => r.id !== id));
            toast.success("Role deleted");
            api.get("/roles-stats/overview").then(({ data }) => setStats(data));
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const aiDescribe = async (id) => {
        setDescribing(id);
        try {
            const { data } = await api.post(`/roles/${id}/ai-describe`);
            setRoles((prev) => prev.map((r) => (r.id === id ? data : r)));
            toast.success("AI description generated");
        } catch (e) {
            toast.error("AI description failed");
        } finally { setDescribing(null); }
    };

    const runConsolidation = async () => {
        setConsolidating(true);
        try {
            const { data } = await api.post("/roles/consolidate");
            setConsolidation(data.suggestions);
            toast.success(`${data.suggestions.length} consolidation opportunit${data.suggestions.length === 1 ? "y" : "ies"} found`);
        } catch (e) {
            toast.error("Consolidation analysis failed");
        } finally { setConsolidating(false); }
    };

    // Bulk "Migrate All": every T-Code-Only role onto real Fiori Business
    // Catalogs in one pass (purely additive, safe to re-run).
    const runMigrateAll = async () => {
        setMigratingAll(true);
        try {
            const { data } = await api.post("/roles/convert-all-to-catalog");
            setMigrateAllResult(data);
            if (data.candidates === 0) {
                toast.info("No T-code-only roles left — every role already carries Fiori catalogs");
            } else {
                toast.success(`${data.migrated}/${data.candidates} role(s) migrated · ${data.avg_coverage_pct}% avg T-code coverage`);
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk migration failed");
        } finally { setMigratingAll(false); }
    };

    const exportCatalog = async (fmt) => {
        setExporting(fmt);
        try {
            const res = await api.get(`/roles/export/${fmt}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `sentinel_forge_role_catalog.${fmt}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${fmt.toUpperCase()} catalog`);
        } catch (e) {
            toast.error("Export failed");
        } finally { setExporting(""); }
    };

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    // Opens the Authorization Matrix Manager for a role, optionally landing
    // directly on a given tab (e.g. "migration" for the T-Code-Only badge's
    // one-click Migrate shortcut).
    const openTools = (role, tab = "org") => {
        setToolsRole(role);
        setToolsInitialTab(tab);
        setToolsOpen(true);
    };

    const simulate = async () => {
        if (selected.size < 1) { toast.error("Select at least one role to simulate"); return; }
        setSimulating(true);
        try {
            const { data } = await api.post("/roles/cross-sod", { role_ids: [...selected] });
            setCrossSod(data);
            toast.success(`${data.cross_role_count} cross-role conflict(s) in the combined access set`);
        } catch (e) {
            toast.error("Simulation failed");
        } finally { setSimulating(false); }
    };

    const filteredMatrix = useMemo(() => {
        if (!rulebook) return [];
        const q = rulebookSearch.trim().toLowerCase();
        return rulebook.matrix.filter((r) => {
            if (bpFilter !== "all" && r.business_process !== bpFilter) return false;
            if (!q) return true;
            return (
                r.id.toLowerCase().includes(q) ||
                r.name.toLowerCase().includes(q) ||
                r.a.label.toLowerCase().includes(q) ||
                r.b.label.toLowerCase().includes(q)
            );
        });
    }, [rulebook, bpFilter, rulebookSearch]);

    const hasUnverifiedMatches = (r) =>
        (r.sod_conflicts || []).some((c) => c.match_confidence === "medium") ||
        (r.critical_access || []).some((c) => c.match_confidence === "medium");

    const visibleRoles = useMemo(() => {
        let list = roles;
        if (ruleFilter) list = list.filter((r) => (r.sod_conflicts || []).some((c) => c.rule_id === ruleFilter));
        if (verificationFilter === "unverified") return list.filter(hasUnverifiedMatches);
        if (verificationFilter === "verified") return list.filter((r) => !hasUnverifiedMatches(r));
        return list;
    }, [roles, verificationFilter, ruleFilter]);

    const verificationCoverage = useMemo(() => {
        if (roles.length === 0) return null;
        const unverifiedCount = roles.filter(hasUnverifiedMatches).length;
        return { total: roles.length, unverified: unverifiedCount, verified: roles.length - unverifiedCount };
    }, [roles]);

    return (
        <ModuleGate module="role_engineering" label="Sentinel Forge — Role Engineering">
        <div className="space-y-6">
            <RoleEngineeringHero exporting={exporting} onExport={exportCatalog} disabled={roles.length === 0} />
            <RoleStatsTiles stats={stats} />

            <Tabs defaultValue="library" className="w-full">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger data-testid={TID.roleTab("library")} value="library">Role Library</TabsTrigger>
                    <TabsTrigger data-testid={TID.roleTab("fiori")} value="fiori">Fiori Apps Library</TabsTrigger>
                    <TabsTrigger data-testid={TID.roleTab("catalog-roles")} value="catalog-roles">Fiori Catalog Roles</TabsTrigger>
                    <TabsTrigger data-testid={TID.roleTab("templates")} value="templates">Role Templates</TabsTrigger>
                    <TabsTrigger data-testid={TID.roleTab("best-practice")} value="best-practice">Best-Practice Sweep</TabsTrigger>
                </TabsList>

                <TabsContent value="best-practice" className="pt-4">
                    <BestPracticeSweepPanel roles={roles} openTools={openTools} isAdmin={!!caps?.is_admin} />
                </TabsContent>

                <TabsContent value="fiori" className="pt-4">
                    <FioriLibraryPanel />
                </TabsContent>

                <TabsContent value="catalog-roles" className="pt-4">
                    <FioriCatalogRolesPanel onGenerated={load} />
                </TabsContent>

                <TabsContent value="templates" className="pt-4">
                    <RoleTemplatesPanel onGenerated={load} />
                </TabsContent>

                <TabsContent value="library" className="space-y-6 pt-4">
                    <SoDRulebookPanel
                        caps={caps}
                        rulebook={rulebook}
                        showRulebook={showRulebook}
                        setShowRulebook={setShowRulebook}
                        showCritical={showCritical}
                        setShowCritical={setShowCritical}
                        bpFilter={bpFilter}
                        setBpFilter={setBpFilter}
                        rulebookSearch={rulebookSearch}
                        setRulebookSearch={setRulebookSearch}
                        filteredMatrix={filteredMatrix}
                    />

                    <MassGenerationPanel
                        specs={specs}
                        setSpecs={setSpecs}
                        generating={generating}
                        generate={generate}
                        loadTemplate={loadTemplate}
                        showActvtSelector={showActvtSelector}
                        setShowActvtSelector={setShowActvtSelector}
                        defaultActvt={defaultActvt}
                        toggleDefaultActvt={toggleDefaultActvt}
                    />

                    {ruleFilter && (
                        <div data-testid="role-rule-filter-chip" className="panel px-3 py-2 rounded-[4px] flex items-center gap-2 border border-rose-500/25">
                            <span className="text-[11px] font-mono text-slate-400">
                                Library filtered to roles violating SoD rule <span className="text-rose-300">{ruleFilter}</span> · {visibleRoles.length} role(s)
                            </span>
                            <button data-testid="role-rule-filter-clear" onClick={clearRuleFilter} className="text-slate-500 hover:text-rose-400 transition-colors" aria-label="Clear rule filter">
                                <X size={13} />
                            </button>
                        </div>
                    )}

                    <RoleLibraryPanel
                        roles={roles}
                        visibleRoles={visibleRoles}
                        loading={loading}
                        selected={selected}
                        toggleSelect={toggleSelect}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        describing={describing}
                        aiDescribe={aiDescribe}
                        remove={remove}
                        openTools={openTools}
                        verificationFilter={verificationFilter}
                        setVerificationFilter={setVerificationFilter}
                        verificationCoverage={verificationCoverage}
                        runConsolidation={runConsolidation}
                        consolidating={consolidating}
                        setBulkEditorOpen={setBulkEditorOpen}
                        runMigrateAll={runMigrateAll}
                        migratingAll={migratingAll}
                    />

                    <MigrateAllResultsPanel result={migrateAllResult} dismiss={() => setMigrateAllResult(null)} />

                    <CrossRoleSimulationPanel
                        selected={selected}
                        simulating={simulating}
                        simulate={simulate}
                        crossSod={crossSod}
                    />

                    <ConsolidationResultsPanel consolidation={consolidation} />
                </TabsContent>
            </Tabs>

            <RoleToolsModal role={toolsRole} open={toolsOpen} onOpenChange={setToolsOpen} onUpdated={load} initialTab={toolsInitialTab} />
        </div>
        </ModuleGate>
    );
}
